import { queryOptions, useQuery } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';
import { SchemaProjectedFundingDto } from '../generated/apiTypes';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { Gwei } from '../lib/gwei';
import { multiply } from '../lib/math';

const REFETCH_INTERVAL = minutesToMilliseconds(5);

export const transformProjectedFunding = (rate: SchemaProjectedFundingDto) => {
  const fundingRate1h = Gwei.toGwei(rate.fundingRate1h);
  const fundingRateProjected1h = Gwei.toGwei(rate.fundingRateProjected1h);

  // Calculate daily and annualized rates from the projected 1h rate
  const fundingRateProjected8h = multiply(fundingRateProjected1h, Gwei.toGwei(8));
  const fundingRateProjected1d = multiply(fundingRateProjected1h, Gwei.toGwei(24));
  const fundingRateProjected1y = multiply(fundingRateProjected1d, Gwei.toGwei(365));

  return {
    fundingRate1h,
    fundingRateProjected1h,
    fundingRateProjected8h,
    fundingRateProjected1d,
    fundingRateProjected1y,
  };
};

export type ProjectedFunding = ReturnType<typeof transformProjectedFunding>;

export const projectedFundingQuery = (productId: string) =>
  queryOptions({
    queryKey: ['GET', '/v1/funding/projected', productId] as const,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: minutesToMilliseconds(1),
    queryFn: async ({ queryKey, signal }) => {
      const [method, url, productId] = queryKey;
      const { data } = await fetchClient[method](url, {
        signal,
        params: { query: { productId } },
      }).then(dataOrThrow);

      return data ? transformProjectedFunding(data) : null;
    },
  });

export const useProjectedFunding = (productId: string) =>
  useQuery({ ...projectedFundingQuery(productId), placeholderData: (oldData) => oldData });
